import React from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'hover:opacity-90 focus:ring-blue-500',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500',
        outline: 'border-2 bg-transparent hover:bg-opacity-10 focus:ring-blue-500',
      },
      size: {
        small: 'text-sm px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

const Button = ({
  // Required parameters with defaults
  text = "Add to cart",
  text_font_size = "16",
  text_font_family = "Lato",
  text_font_weight = "700",
  text_line_height = "20px",
  text_text_align = "left",
  text_color = "#ffffff",
  fill_background_color = "#053c21",
  border_border_radius = "8px",
  
  // Optional parameters (no defaults)
  effect_box_shadow,
  fill_background,
  border_border,
  border_border_image,
  layout_align_self,
  layout_width,
  padding,
  margin,
  position,
  layout_gap,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  ...props
}) => {
  // Safe validation for optional parameters
  const hasValidBoxShadow = effect_box_shadow && typeof effect_box_shadow === 'string' && effect_box_shadow?.trim() !== '';
  const hasValidBackground = fill_background && typeof fill_background === 'string' && fill_background?.trim() !== '';
  const hasValidBorder = border_border && typeof border_border === 'string' && border_border?.trim() !== '';
  const hasValidBorderImage = border_border_image && typeof border_border_image === 'string' && border_border_image?.trim() !== '';
  const hasValidAlignSelf = layout_align_self && typeof layout_align_self === 'string' && layout_align_self?.trim() !== '';
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width?.trim() !== '';
  const hasValidPadding = padding && typeof padding === 'string' && padding?.trim() !== '';
  const hasValidMargin = margin && typeof margin === 'string' && margin?.trim() !== '';
  const hasValidPosition = position && typeof position === 'string' && position?.trim() !== '';
  const hasValidGap = layout_gap && typeof layout_gap === 'string' && layout_gap?.trim() !== '';

  // Build optional Tailwind classes
const optionalClasses = [
  hasValidBoxShadow ? `shadow-[${effect_box_shadow}]` : '',
  hasValidAlignSelf ? `self-${layout_align_self}` : '',
  hasValidWidth ? `w-[${layout_width}]` : '',
  hasValidPadding ? `p-[${padding}]` : '',
  hasValidMargin ? `m-[${margin}]` : '',
  hasValidPosition ? position : '',
  hasValidGap ? `gap-[${layout_gap}]` : '',
  `rounded-md`, // إضافة خاصية الحواف لتجربة التأثير من Tailwind
].filter(Boolean).join(' ');


  // Build inline styles for required parameters
  const buttonStyles = {
    fontSize: `${text_font_size}px`,
    fontFamily: text_font_family,
    fontWeight: text_font_weight,
    lineHeight: text_line_height,
    textAlign: text_text_align,
    color: text_color,
    backgroundColor: hasValidBackground ? fill_background : fill_background_color,
    border: hasValidBorder ? border_border : 'none',
    borderRadius: border_border_radius,
    borderImage: hasValidBorderImage ? border_border_image : 'none',
  };

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={buttonStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;